/**
 * The session the ledger renders. Shape per the layout spec
 * (docs/review/foxet-landing-layouts-2026-10-15.html, "Component").
 *
 * The ledger renders a captured session; it does not invent one. Until the
 * capture lands, `placeholder: true` keeps the band labelled as such.
 */
export type FlowStatus = 'verified' | 'narrated';

export type EventIcon = 'globe' | 'cta' | 'user-plus' | 'captured';

export type EyeMode = 'neutral' | 'look' | 'wide' | 'squint' | 'sparkle' | 'double-blink' | 'half-lid' | 'glance' | 'exit' | 'wink';

/**
 * Mr. Clippy's placement and behaviour per step; x/y are percentages of the pane's view. He is the
 * BUYER in this story (a busy head of RevOps: curious, a little skeptical, quick to warm up when
 * something works, never sarcastic); Foxy, the fox mark, is the expert. He arrives from outside the
 * ledger (act 'arrive'), lives through the session on the left side of the frame, is qualified
 * ('qualified') and leaves through the pane's bottom-right ('exit') to rest at the next section.
 */
export type Clippy = {
  x: number;
  y: number;
  action: 'jump-in' | 'hop' | 'point' | 'idle';
  act: 'arrive' | 'session' | 'qualified' | 'exit';
  /** his voice: ≤ 6 words, first person, present tense, one per beat */
  say?: string;
  target?: { x: number; y: number }; // 'point' only
  emote?: 'wave' | 'curious' | 'nod' | 'cheer' | 'speak' | 'listen' | 'think' | 'point'; // one-shot per step
  eyes?: EyeMode; // default: look at the target (or at Foxy in the greeting)
  skeptic?: number; // 0..1 lids at rest: narrow at arrival, neutral by the Leads frame
  lean?: number; // degrees: −6 leaning back at the start, +6 leaning in by the brief
  breath?: number; // seconds: impatient 2.4 until the Hindi switch, 3.2 after
  followUpEmail?: boolean; // exit only: the drafted follow-up chases him out
  stay?: boolean; // no movement on this step (B8, B9, B11): eyes, speech and in-place motion only
};

/** Where an artifact goes: the chip Foxy tosses toward its card. Jira is direction only. */
export type Destination = 'salesforce' | 'hubspot' | 'slack' | 'jira' | 'insights' | null;

export type Step = {
  t: string; // "00:41"; events before the call use negative offsets ("−0:40")
  kind: 'event' | 'buyer' | 'foxet'; // event = top-of-funnel row before the first line
  text: string; // verbatim, lightly edited
  icon?: EventIcon; // events only
  frame?: string; // real screenshot of the driven product (or the public entry page)
  frameCaption?: string; // "Leads · the real dashboard"
  pane?: 'greeting'; // show the greeting state instead of the last frame seen
  flow?: { name: string; status: FlowStatus; verifiedAt?: string };
  artifactIds?: string[]; // artifacts that become visible here
  clippy?: Clippy; // Mr. Clippy on this step; a real capture must supply its own
  foxySay?: string; // Foxy's bubble, sparing and warm
  foxyEyes?: EyeMode;
};

export type Artifact = { destination: Destination } & (
  | { id: string; kind: 'signal'; title: string; body: string }
  | { id: string; kind: 'flag'; title: string; body: string }
  | { id: string; kind: 'followup'; title: string; body: string; held: true }
  | { id: string; kind: 'brief'; title: string; fields: Record<string, string> }
  | { id: string; kind: 'crm'; system: 'salesforce' | 'hubspot' | 'zoho'; body: string }
);

export type Session = {
  product: { name: string; url: string }; // "Foxet.ai", "app.foxet.ai"
  /** The expert's name. Foxet ships one mascot, Foxy, by default; tenants may rename theirs, and a
   *  real capture carries the tenant's name. */
  agent: string;
  languages: string[]; // ["en","hi"] as used
  recordedAt: string; // ISO, shown as local time
  steps: Step[];
  artifacts: Artifact[];
  /** True until a real capture replaces this data. Renders the warning label. */
  placeholder: boolean;
  /** Plain page with the unedited transcript. Absent until the capture lands. */
  transcriptHref?: string;
};

const frames = {
  landing: '/media/placeholder-session/landing-sample.jpg',
  cta: '/media/placeholder-session/cta-sample.jpg',
  entry: '/media/placeholder-session/entry-sample.jpg',
  leads: '/media/placeholder-session/leads-sample.jpg',
  brief: '/media/placeholder-session/brief-sample.jpg',
};

/**
 * PLACEHOLDER. Transcript written for the layout mock. Frames are 1600×900 @2×
 * screenshots of the real dashboard on the local test tenant with its seeded
 * sample data (Leads filtered to synthetic rows; one brief with its draft
 * follow-up), not per-step captures from a session. Steps without a frame of
 * their own keep the last frame seen. Replace with the captured session and set
 * placeholder: false.
 */
export const session: Session = {
  placeholder: true,
  product: { name: 'Foxet.ai', url: 'app.foxet.ai' },
  agent: 'Foxy',
  languages: ['en', 'hi'],
  recordedAt: '',
  steps: [
    // Top of the funnel, one real screen per step: this build's /pre-sales page as the campaign landing
    // page, the same page with its primary call to action pressed, the public entry form (app.foxet.ai/l/…,
    // unauthenticated), and the dashboard Leads view with sample data.
    { t: '−0:40', kind: 'event', icon: 'globe', text: 'A buyer lands on a campaign landing page', frame: frames.landing, frameCaption: 'Campaign landing page · Sales & Pre-sales · real screen', clippy: { x: 31, y: 85, action: 'jump-in', act: 'arrive', emote: 'wave', say: 'Right. Let’s see this.', skeptic: 0.35, lean: -6, breath: 2.4 } },
    // (no widget window in the ledger: it reads as a chatbot and dilutes the value proposition)
    { t: '−0:25', kind: 'event', icon: 'cta', text: 'Clicks See Foxet in action', frame: frames.cta, frameCaption: 'Clicks the call to action · real screen', clippy: { x: 38, y: 84, action: 'point', act: 'session', target: { x: 27, y: 83 }, emote: 'curious', say: 'Hm. Let’s see.', skeptic: 0.3, lean: -6, breath: 2.4 } },
    { t: '−0:10', kind: 'event', icon: 'user-plus', text: 'Lead page · a short form', frame: frames.entry, frameCaption: 'Lead page · the real entry form', clippy: { x: 30, y: 60, action: 'point', act: 'session', target: { x: 56, y: 39 }, emote: 'nod', say: 'Three fields. Fine.', skeptic: 0.25, lean: -4, breath: 2.4 } },
    { t: '0:00', kind: 'event', icon: 'captured', text: 'Lead captured · the session starts', frame: frames.leads, frameCaption: 'Leads · the lead lands in your dashboard', artifactIds: ['lead'], clippy: { x: 24, y: 80, action: 'point', act: 'session', target: { x: 37, y: 43 }, emote: 'cheer', eyes: 'wide', say: 'Didn’t hurt.', skeptic: 0.15, lean: -3, breath: 2.4 } },
    {
      t: '00:00',
      kind: 'buyer',
      text: 'We get a lot of inbound. Where does this actually help us?',
      pane: 'greeting',
      frameCaption: 'Greeting',
      clippy: { x: 42, y: 42, action: 'idle', act: 'session', emote: 'speak', skeptic: 0.15, lean: -2, breath: 2.4 },
    },
    {
      t: '00:06',
      kind: 'foxet',
      text: 'Let me show you how a prospect’s first conversation becomes a brief for your team. Which product do you sell?',
      clippy: { x: 42, y: 42, action: 'idle', act: 'session', emote: 'listen', say: 'Go on.', skeptic: 0.1, lean: 0, breath: 2.4 },
    },
    {
      t: '00:22',
      kind: 'foxet',
      text: 'This is the Leads view. Every conversation lands here with the question that started it and the role of the person asking.',
      frame: frames.leads,
      frameCaption: 'Leads · the real dashboard',
      flow: { name: 'track & categorise prospects', status: 'verified', verifiedAt: '03:05 UTC' },
      artifactIds: ['signal-inbound'],
      clippy: { x: 22, y: 30, action: 'point', act: 'session', target: { x: 46, y: 46 }, emote: 'point', say: 'So that’s where they land.', skeptic: 0, lean: 0, breath: 2.4, stay: true },
    },
    {
      t: '00:41',
      kind: 'buyer',
      text: 'Can it do this in Hindi?',
      flow: { name: 'track & categorise prospects', status: 'verified', verifiedAt: '03:05 UTC' },
      clippy: { x: 22, y: 30, action: 'idle', act: 'session', emote: 'speak', say: 'Let’s test that.', skeptic: 0, lean: 0, breath: 2.4, stay: true },
    },
    {
      t: '00:44',
      kind: 'foxet',
      text: 'हाँ। आइए, मैं यही फ़्लो हिंदी में जारी रखती हूँ। (switches language, continues the flow)',
      frameCaption: 'Leads · continuing in Hindi',
      flow: { name: 'track & categorise prospects', status: 'verified', verifiedAt: '03:05 UTC' },
      artifactIds: ['signal-hindi'],
      clippy: { x: 22, y: 30, action: 'idle', act: 'session', emote: 'listen', eyes: 'wide', say: 'Huh. It can.', skeptic: 0, lean: 2, breath: 3.2, stay: true },
      foxySay: 'I’ll keep the context.',
    },
    {
      t: '01:30',
      kind: 'buyer',
      text: 'Show me the export.',
      flow: { name: 'export report', status: 'narrated' },
      artifactIds: ['flag-export'],
      clippy: { x: 22, y: 30, action: 'idle', act: 'session', emote: 'speak', eyes: 'squint', skeptic: 0, lean: 2, breath: 3.2, stay: true },
    },
    {
      t: '01:33',
      kind: 'foxet',
      text: 'That flow hasn’t been verified against the live product in the last 72 hours, so I won’t drive it. Here is what it does, and I’ll flag it for your team.',
      flow: { name: 'export report', status: 'narrated' },
      clippy: { x: 22, y: 30, action: 'idle', act: 'session', emote: 'think', eyes: 'squint', say: 'Not verified. Honest.', skeptic: 0, lean: 2, breath: 3.2, stay: true },
      foxySay: 'Not verified, so I won’t drive it.',
    },
    {
      t: '02:10',
      kind: 'foxet',
      text: 'I’ve drafted a follow-up with the two questions you raised. It goes to your account team for review, not to you, until they approve it.',
      frame: frames.brief,
      frameCaption: 'Brief · draft follow-up, held for review',
      flow: { name: 'track & categorise prospects', status: 'verified', verifiedAt: '03:05 UTC' },
      artifactIds: ['followup'],
      clippy: { x: 34, y: 50, action: 'point', act: 'session', target: { x: 50, y: 70 }, emote: 'point', say: 'Held for review. Good.', skeptic: 0, lean: 6, breath: 3.2 },
      foxySay: 'Drafted. Your team reviews it.',
    },
    {
      t: '02:24',
      kind: 'foxet',
      text: 'The brief and your contact are in Salesforce.',
      flow: { name: 'track & categorise prospects', status: 'verified', verifiedAt: '03:05 UTC' },
      artifactIds: ['brief'],
      clippy: { x: 34, y: 50, action: 'idle', act: 'qualified', emote: 'cheer', say: 'Already in my CRM.', skeptic: 0, lean: 6, breath: 3.2, stay: true },
    },
    {
      t: '02:31',
      kind: 'foxet',
      text: 'Want me to book a time with the team?',
      frameCaption: 'Follow-up drafted · held for review',
      flow: { name: 'track & categorise prospects', status: 'verified', verifiedAt: '03:05 UTC' },
      artifactIds: ['crm'],
      clippy: { x: 34, y: 50, action: 'idle', act: 'exit', target: { x: 50, y: 70 }, say: 'Book me.', skeptic: 0, lean: 6, breath: 3.2, followUpEmail: true },
    },
  ],
  artifacts: [
    // destinations: the lead and the brief go where the brief is pushed (Salesforce, consistently);
    // signals go to Insights; the flagged flow goes to Slack today — Jira will take flagged flows
    // when that integration ships (it is direction only; a 'jira' destination renders "Jira · soon");
    // the follow-up is held, so nothing is sent and it gets no toss.
    { id: 'lead', kind: 'signal', title: 'Lead captured', body: 'Name, work email, company.', destination: 'salesforce' },
    { id: 'signal-inbound', kind: 'signal', title: 'Demand signal', body: 'Inbound volume; wants to see the lead view first.', destination: 'insights' },
    { id: 'signal-hindi', kind: 'signal', title: 'Demand signal', body: 'Asked for Hindi. Language held for the rest of the call.', destination: 'insights' },
    { id: 'flag-export', kind: 'flag', title: 'Flagged for the team', body: '“Export report” not verified in 72 h. Narrated, not driven.', destination: 'slack' },
    { id: 'followup', kind: 'followup', title: 'Follow-up draft', body: 'Held for review. Not sent.', held: true, destination: null },
    {
      id: 'brief',
      kind: 'brief',
      title: 'Brief for Sales',
      fields: { Role: 'Head of RevOps', Process: 'inbound to SDR queue', Open: 'regional approvals; export', Next: '30-min evaluation' },
      destination: 'salesforce',
    },
    { id: 'crm', kind: 'crm', system: 'salesforce', body: 'Contact + brief pushed', destination: 'salesforce' },
  ],
};
